'use client';

import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import Image from 'next/image';
import { stats } from '@/config/stats';
import ScrollReveal from '@/components/ScrollReveal';
import { DotGlow } from '@/components/ui/dot-glow';
import dynamic from 'next/dynamic';
import HeroTexture, { homeCardTexture, reconCtaTexture } from '@/app/recon/_components/HeroTexture';
import HomeBorrowerTable from '@/components/illustrations/HomeBorrowerTable';
import HomeHiddenRisks from '@/components/illustrations/HomeHiddenRisks';
import HomeInvestorTracking from '@/components/illustrations/HomeInvestorTracking';
const VideoModal = dynamic(() => import('@/components/VideoModal'), { ssr: false });

export default function Home() {
    return (
        <>
            <Navigation variant="home" />

            {/* Hero Section */}
            <section data-nav-theme="dark" className="ex-hero-banner home-hero relative flex flex-col justify-center border-zinc-800 border-b overflow-hidden">
                {/* Background Image */}
                <Image
                    src="/api/hero-background"
                    alt=""
                    fill
                    priority
                    quality={75}
                    sizes="100vw"
                    className="object-cover -z-10"
                />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgb(9_9_11/0.85)_0%,rgb(9_9_11/0.75)_25%,rgb(9_9_11/0.5)_50%,transparent_70%)] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-6 w-full z-10">
                    <div className="max-w-3xl">
                        <h1 className="ex-hero mb-6 fade-in-up delay-100 text-white">
                            Find Your Best Borrowers.<br />
                            <span className="ex-hero-line2">Fund with Confidence.</span>
                        </h1>

                        <p className="ex-hero-sub max-w-2xl mb-10 fade-in-up delay-200">
                            Elementix gives <span className="text-white">private lenders</span> complete visibility into borrower and market activity.
                        </p>

                        <div className="ex-hero-actions flex flex-col sm:flex-row gap-4 fade-in-up delay-300">
                            <div className="ex-glow-action w-full sm:w-auto">
                                <span aria-hidden="true" className="ex-glow" />
                                <a href="/contact" className="ex-button ex-button-light w-full sm:w-auto">
                                    Book a Demo
                                    <span className="iconify solar--arrow-right-linear"></span>
                                </a>
                            </div>
                            <VideoModal
                                videoSrc="https://www.youtube.com/embed/ylq8iTc9uXc?si=A2ZonnnlvUj9fvjt"
                                buttonClassName="ex-hero-video w-full sm:w-auto px-6 py-3 bg-zinc-800 text-white text-sm font-medium rounded-sm hover:bg-zinc-700 transition-all flex items-center justify-center gap-2 border border-zinc-600"
                                buttonLabel="30s Video Demo"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Recon-style interlocking cards (app/globals.css) */}
            <div className="ex-cards">
            <div className="ex-cards-grid">

            {/* Credibility Bar */}
            <section className="ex-card border-b border-zinc-200 bg-zinc-50 min-h-[200px] md:min-h-[140px] flex items-center">
                <div className="max-w-7xl mx-auto px-6 w-full py-8 md:py-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="text-center md:text-left">
                            <div className="ex-kpi text-3xl font-medium text-zinc-900 tracking-tight mb-1 font-mono">
                                {stats.documentsAnalyzed}
                            </div>
                            <div className="ex-eyebrow">
                                Documents Analyzed
                            </div>
                        </div>
                        <div className="text-center md:text-left">
                            <div className="ex-kpi text-3xl font-medium text-zinc-900 tracking-tight mb-1 font-mono">
                                {stats.ownersTracked}
                            </div>
                            <div className="ex-eyebrow">
                                Owners Tracked
                            </div>
                        </div>
                        <div className="text-center md:text-left">
                            <div className="ex-kpi text-3xl font-medium text-zinc-900 tracking-tight mb-1 font-mono">
                                {stats.operatingEntities}
                            </div>
                            <div className="ex-eyebrow">
                                Entities Mapped
                            </div>
                        </div>
                        <div className="text-center md:text-left">
                            <div className="ex-kpi text-3xl font-medium text-zinc-900 tracking-tight mb-1 font-mono">
                                {stats.coverage}
                            </div>
                            <div className="ex-eyebrow">
                                US Coverage
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <div className="ex-cards-contents bg-zinc-50 overflow-hidden">
                {/* Capability 1: Prospecting */}
                <section id="prospecting" className="ex-card ex-section border-b border-zinc-200">
                    <HeroTexture settings={homeCardTexture} className="ex-card-texture ex-card-texture-right" />
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
                            {/* Text Content */}
                            <div className="order-1 lg:order-1">
                                <ScrollReveal>
                                    <h2 className="ex-h2 text-zinc-900 mb-4">
                                        Find active borrowers in your credit box
                                    </h2>
                                </ScrollReveal>
                                <ScrollReveal delay={200}>
                                    <p className="ex-body mb-8">
                                        Target active investors based on their track record and recent
                                        activity. Reach them directly with verified contact information.
                                    </p>
                                </ScrollReveal>
                                <ScrollReveal delay={300}>
                                    <a href="/prospecting" className="ex-button">
                                        Explore Prospecting
                                        <span className="iconify solar--arrow-right-linear ml-2 text-sm"></span>
                                    </a>
                                </ScrollReveal>
                            </div>

                            {/* Visual Interface */}
                            <DotGlow className="ex-dotglow order-2 lg:order-2">
                                <ScrollReveal delay={100}>
                                    <HomeBorrowerTable />
                                </ScrollReveal>
                            </DotGlow>
                        </div>
                    </div>
                </section>

                {/* Capability 2: Risk */}
                <section id="risk" className="ex-card ex-section border-b border-zinc-200 bg-white">
                    <HeroTexture settings={homeCardTexture} className="ex-card-texture ex-card-texture-left" />
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
                            {/* Visual Interface */}
                            <DotGlow className="ex-dotglow order-2 lg:order-1 h-full">
                                <HomeHiddenRisks />
                            </DotGlow>

                            {/* Text Content */}
                            <div className="order-1 lg:order-2">
                                <ScrollReveal>
                                    <h2 className="ex-h2 text-zinc-900 mb-4">
                                        Surface hidden risks across all borrower entities
                                    </h2>
                                </ScrollReveal>
                                <ScrollReveal delay={200}>
                                    <p className="ex-body mb-8">
                                        Verify borrower experience and uncover undisclosed liabilities,
                                        foreclosures, and overexposure across their entire portfolio.
                                    </p>
                                </ScrollReveal>
                                <ScrollReveal delay={300}>
                                    <a href="/underwriting" className="ex-button">
                                        Explore Underwriting
                                        <span className="iconify solar--arrow-right-linear ml-2 text-sm"></span>
                                    </a>
                                </ScrollReveal>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Capability 3: Investor Tracking */}
                <section id="tracking" className="ex-card ex-section border-b border-zinc-200">
                    <HeroTexture settings={homeCardTexture} className="ex-card-texture ex-card-texture-right" />
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
                            {/* Text Content */}
                            <div className="order-1 lg:order-1">
                                <ScrollReveal>
                                    <h2 className="ex-h2 text-zinc-900 mb-4">
                                        Track investor portfolios and activity at scale
                                    </h2>
                                </ScrollReveal>
                                <ScrollReveal delay={200}>
                                    <p className="ex-body mb-8">
                                        Search for investors by portfolio characteristics. Monitor
                                        signals like expansion, sell-off, and geographic shifts across
                                        millions of investors.
                                    </p>
                                </ScrollReveal>
                                <ScrollReveal delay={300}>
                                    <a href="/investor-tracking" className="ex-button">
                                        Explore Investor Tracking
                                        <span className="iconify solar--arrow-right-linear ml-2 text-sm"></span>
                                    </a>
                                </ScrollReveal>
                            </div>

                            {/* Visual Interface */}
                            <DotGlow className="ex-dotglow order-2 lg:order-2">
                                <ScrollReveal delay={100}>
                                    <HomeInvestorTracking />
                                </ScrollReveal>
                            </DotGlow>
                        </div>
                    </div>
                </section>
            </div>

            {/* What We Do */}
            <section className="ex-card ex-cta ex-section bg-white border-b border-zinc-200">
                <HeroTexture settings={reconCtaTexture} className="ex-cta-texture" />
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <ScrollReveal delay={100}>
                        <h2 className="ex-cta-title">Learn how we build ownership intelligence</h2>
                        <p className="ex-cta-statement text-xl md:text-2xl font-light leading-relaxed text-zinc-900 mb-8">
                            Real estate investors operate through layers of holding companies.&nbsp;
                            <span className="font-medium">Elementix</span>
                            {' '}connects them to every entity they control, providing complete
                            visibility into their activity and track records.
                        </p>
                    </ScrollReveal>
                    <ScrollReveal delay={200}>
                        <div className="ex-glow-action">
                            <span aria-hidden="true" className="ex-glow" />
                            <a href="/technology" className="ex-button">
                                See our technology
                                <span className="iconify solar--arrow-right-linear ml-2 text-sm"></span>
                            </a>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            </div>
            </div>

            <Footer />
        </>
    );
}
